import { csiAPI } from "src/boot/axios"
import { CSIurl } from "src/util/enums/urls"
import {
  appendAttachmentToFormData,
  concatUrl,
  getCustomConfig,
  getNewAbortHandler,
  handleAttachmentBatchResponses,
} from "src/util/http/httpHelper"
import { Attachment, AttachmentUpload } from "src/util/types/dataModels/attachment"
import {
  ItemAttributeCharValue,
  ItemAttributeNumericValue,
  ItemAttributeSelectValue,
  ItemAttributeTemporalValue,
} from "src/util/types/dataModels/attribute"
import {
  BatchErrors,
  ObjectList,
  PageParams,
  PayloadEntity,
} from "src/util/types/dataModels/genericObject"
import { HttpConfig } from "src/util/types/dataModels/http"
import { Item, ItemFilterParams, ItemSearchQueryID } from "src/util/types/dataModels/item"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_DELETE,
  PERMISSION_ACCESS_READ,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_ATTRIBUTES,
  PERMISSION_ITEMS,
} from "src/util/types/dataModels/user"
import { WorkOrderRegularItem } from "src/util/types/dataModels/workOrder"

//region Items

/**
 * Return the list of all active Items
 *
 * @param config custom configurations and filtering params
 */
export async function getItemsList(
  config: HttpConfig<ItemFilterParams> = {}
): Promise<ObjectList<Item>> {
  const entityURL = CSIurl.ITEMS
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Create a new Item
 *
 * @param config custom configurations and filtering params
 * @param item
 */
export async function postItem(config: HttpConfig = {}, item: PayloadEntity<Item>): Promise<Item> {
  const url = CSIurl.ITEMS
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, item, { signal, customConfig })

  return data
}

/**
 * Return an individual Item
 *
 * @param config custom configurations and filtering params
 * @param id
 */
export async function getItem(config: HttpConfig = {}, id: string): Promise<Item> {
  const url = CSIurl.ITEMS_ID.replace("{id}", id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Replace an existing individual Item
 *
 * @param config custom configurations and filtering params
 * @param itemId
 * @param item
 */
export async function putItem(
  config: HttpConfig = {},
  itemId: string,
  item: PayloadEntity<Item>
): Promise<Item> {
  const url = CSIurl.ITEMS_ID.replace("{id}", itemId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, item, { signal, customConfig })

  return data
}

/**
 * (Partially) update an individual Item
 *
 * @param config custom configurations and filtering params
 * @param itemId
 * @param item
 */
export async function patchItem(
  config: HttpConfig = {},
  itemId: string,
  item: Partial<PayloadEntity<Item>>
): Promise<Item> {
  const url = CSIurl.ITEMS_ID.replace("{id}", itemId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, item, { signal, customConfig })

  return data
}

//endregion

//region Item Char Values

export async function getItemAttributeCharValue(
  config: HttpConfig = {},
  itemId: string,
  attributeId: string
): Promise<ItemAttributeCharValue> {
  const url = CSIurl.ITEMS_ATTRIBUTES_CHAR_VALUE.replace("{item_id}", itemId).replace(
    "{attr_id}",
    attributeId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function patchItemAttributeCharValue(
  config: HttpConfig = {},
  itemId: string,
  attributeId: string,
  payload: Partial<PayloadEntity<ItemAttributeCharValue>>
): Promise<ItemAttributeCharValue> {
  const url = CSIurl.ITEMS_ATTRIBUTES_CHAR_VALUE.replace("{item_id}", itemId).replace(
    "{attr_id}",
    attributeId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, payload, { signal, customConfig })

  return data
}

export async function deleteItemAttributeCharValue(
  config: HttpConfig = {},
  itemId: string,
  attributeId: string
) {
  const url = CSIurl.ITEMS_ATTRIBUTES_CHAR_VALUE.replace("{item_id}", itemId).replace(
    "{attr_id}",
    attributeId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_DELETE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

export async function getItemAttributeCharValues(
  config: HttpConfig = {},
  itemId: string
): Promise<ObjectList<ItemAttributeCharValue>> {
  const url = CSIurl.ITEMS_ATTRIBUTES_CHAR_VALUES.replace("{item_id}", itemId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

//endregion

//region Item Numeric Values

export async function getItemAttributeNumericValue(
  config: HttpConfig = {},
  itemId: string,
  attributeId: string
): Promise<ItemAttributeNumericValue> {
  const url = CSIurl.ITEMS_ATTRIBUTES_NUMERIC_VALUE.replace("{item_id}", itemId).replace(
    "{attr_id}",
    attributeId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function patchItemAttributeNumericValue(
  config: HttpConfig = {},
  itemId: string,
  attributeId: string,
  payload: Partial<PayloadEntity<ItemAttributeNumericValue>>
): Promise<ItemAttributeNumericValue> {
  const url = CSIurl.ITEMS_ATTRIBUTES_NUMERIC_VALUE.replace("{item_id}", itemId).replace(
    "{attr_id}",
    attributeId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, payload, { signal, customConfig })

  return data
}

export async function deleteItemAttributeNumericValue(
  config: HttpConfig = {},
  itemId: string,
  attributeId: string
) {
  const url = CSIurl.ITEMS_ATTRIBUTES_NUMERIC_VALUE.replace("{item_id}", itemId).replace(
    "{attr_id}",
    attributeId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_DELETE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

export async function getItemAttributeNumericValues(
  config: HttpConfig = {},
  itemId: string
): Promise<ObjectList<ItemAttributeNumericValue>> {
  const url = CSIurl.ITEMS_ATTRIBUTES_NUMERIC_VALUES.replace("{item_id}", itemId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

//endregion

//region Item Select Values

export async function getItemAttributeSelectValue(
  config: HttpConfig = {},
  itemId: string,
  attributeId: string
): Promise<ItemAttributeSelectValue> {
  const url = CSIurl.ITEMS_ATTRIBUTES_SELECT_VALUE.replace("{item_id}", itemId).replace(
    "{attr_id}",
    attributeId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function patchItemAttributeSelectValue(
  config: HttpConfig = {},
  itemId: string,
  attributeId: string,
  payload: Partial<PayloadEntity<ItemAttributeSelectValue>>
): Promise<ItemAttributeSelectValue> {
  const url = CSIurl.ITEMS_ATTRIBUTES_SELECT_VALUE.replace("{item_id}", itemId).replace(
    "{attr_id}",
    attributeId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, payload, { signal, customConfig })

  return data
}

// TODO-NOTE: Currently throws 500, cloud team is aware
export async function deleteItemAttributeSelectValue(
  config: HttpConfig = {},
  itemId: string,
  attributeId: string
) {
  const url = CSIurl.ITEMS_ATTRIBUTES_SELECT_VALUE.replace("{item_id}", itemId).replace(
    "{attr_id}",
    attributeId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_DELETE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

export async function getItemAttributeSelectValues(
  config: HttpConfig = {},
  itemId: string
): Promise<ObjectList<ItemAttributeSelectValue>> {
  const url = CSIurl.ITEMS_ATTRIBUTES_SELECT_VALUES.replace("{item_id}", itemId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

//endregion

//region Item Temporal Values

export async function getItemAttributeTemporalValue(
  config: HttpConfig = {},
  itemId: string,
  attributeId: string
): Promise<ItemAttributeTemporalValue> {
  const url = CSIurl.ITEMS_ATTRIBUTES_TEMPORAL_VALUE.replace("{item_id}", itemId).replace(
    "{attr_id}",
    attributeId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function patchItemAttributeTemporalValue(
  config: HttpConfig = {},
  itemId: string,
  attributeId: string,
  payload: Partial<PayloadEntity<ItemAttributeTemporalValue>>
): Promise<ItemAttributeTemporalValue> {
  const url = CSIurl.ITEMS_ATTRIBUTES_TEMPORAL_VALUE.replace("{item_id}", itemId).replace(
    "{attr_id}",
    attributeId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, payload, { signal, customConfig })

  return data
}

export async function deleteItemAttributeTemporalValue(
  config: HttpConfig = {},
  itemId: string,
  attributeId: string
) {
  const url = CSIurl.ITEMS_ATTRIBUTES_TEMPORAL_VALUE.replace("{item_id}", itemId).replace(
    "{attr_id}",
    attributeId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_DELETE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

export async function getItemAttributeTemporalValues(
  config: HttpConfig = {},
  itemId: string
): Promise<ObjectList<ItemAttributeTemporalValue>> {
  const url = CSIurl.ITEMS_ATTRIBUTES_TEMPORAL_VALUES.replace("{item_id}", itemId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

//endregion

//region All Values for Item

export async function getItemAttributeCharValuesList(
  config: HttpConfig = {}
): Promise<ObjectList<ItemAttributeCharValue>> {
  const entityURL = CSIurl.ITEMS_ATTRIBUTES_CHAR_VALUES_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function getItemAttributeNumericValuesList(
  config: HttpConfig = {}
): Promise<ObjectList<ItemAttributeNumericValue>> {
  const entityURL = CSIurl.ITEMS_ATTRIBUTES_NUMERIC_VALUES_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function getItemAttributeSelectValuesList(
  config: HttpConfig = {}
): Promise<ObjectList<ItemAttributeSelectValue>> {
  const entityURL = CSIurl.ITEMS_ATTRIBUTES_SELECT_VALUES_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function getItemAttributeTemporalValuesList(
  config: HttpConfig = {}
): Promise<ObjectList<ItemAttributeTemporalValue>> {
  const entityURL = CSIurl.ITEMS_ATTRIBUTES_TEMPORAL_VALUES_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

//endregion

//region Item Batch

/**
 * Create Items in bulk
 *
 * @param config custom configurations and filtering params
 * @param items
 */
export async function postItemsBatch(
  config: HttpConfig = {},
  items: PayloadEntity<Item>[]
): Promise<ObjectList<Item>> {
  const url = CSIurl.ITEMS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, items, { signal, customConfig })

  return data
}

/**
 * (Partially) update Items in bulk
 * The primary keys of the items, which are being updated
 * must be included as part of the payload data.
 *
 * @param config custom configurations and filtering params
 * @param items
 */
export async function patchItemsBatch(
  config: HttpConfig = {},
  items: Partial<Item>[]
): Promise<ObjectList<Item>> {
  const url = CSIurl.ITEMS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, items, { signal, customConfig })

  return data
}

//endregion

//region Item Search

/**
 * An asynchronous 'search' interface to search item attributes and it's values.
 *
 * Get result from search by calling {@link getItemsSearchListResult}.
 *
 * Accepts either a single or multiple search parameters for filtering Items.
 * All Django Query set lookups are supported (see the full list at
 * https://docs.djangoproject.com/en/3.2/ref/models/querysets/#field-lookups).
 *
 * The attribute type can be one of: "numeric", "char", "temporal", "select".
 *
 * Ex1: If we want to search for Items whose attribute type is char and the id is 3 where the value contains
 *      the word "north", the query should be "attr-char-6__icontains=north".
 *
 * Ex2: If we want to search for Items whose attribute type is numeric and the id is 4 where the value should
 *      be greater than 800 and less than 1000, the query should be "attr-numeric-4__gt=800__lt=1000".
 *      In here we chain search parameters.
 *
 * @param config custom configurations and filtering params
 * @param query
 */
export async function getItemsSearchList(
  config: HttpConfig = {},
  query: string
): Promise<ItemSearchQueryID> {
  const url = CSIurl.ITEMS_SEARCH.concat("?", query)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * List of items that the async search returns.
 *
 * @param config custom configurations and filtering params
 * @param query_id
 * @param pageParams
 */
export async function getItemsSearchListResult(
  config: HttpConfig = {},
  query_id: ItemSearchQueryID,
  pageParams?: PageParams
): Promise<ObjectList<Item>> {
  const url = concatUrl(CSIurl.ITEMS_SEARCH, { ...query_id, ...pageParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

//endregion

//region Attachments

export async function getItemAttachmentList(
  config: HttpConfig = {},
  item_id: string
): Promise<ObjectList<Attachment>> {
  const entityURL = CSIurl.ITEMS_ID_ATTACHMENTS_LIST.replace("{item_id}", item_id)
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: ObjectList<Attachment> } = await csiAPI.get(url, { signal, customConfig })

  data.results = data.results.map((i) => {
    i.context = ["item"]
    return i
  })

  return data
}

export async function postItemAttachment(
  config: HttpConfig = {},
  item_id: string,
  attachment: AttachmentUpload
): Promise<Attachment> {
  const url = CSIurl.ITEMS_ID_ATTACHMENTS_LIST.replace("{item_id}", item_id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const formData = appendAttachmentToFormData(attachment)

  const { data }: { data: Attachment } = await csiAPI.post(url, formData, {
    headers,
    signal,
    customConfig,
  })

  data.context = ["item"]

  return data
}

export async function getItemAttachment(
  config: HttpConfig = {},
  item_id: string,
  attachmentId: string
): Promise<Attachment> {
  const url = CSIurl.ITEMS_ID_ATTACHMENTS_ID.replace("{item_id}", item_id).replace(
    "{id}",
    attachmentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: Attachment } = await csiAPI.get(url, { signal, customConfig })

  data.context = ["item"]

  return data
}

/**
 * Delete an Item Attachment
 *
 * @param config custom configurations and filtering params
 * @param itemId ID of the Item where the Attachment belongs to
 * @param attachmentId ID of the Attachment
 */
export async function deleteItemAttachment(
  config: HttpConfig = {},
  itemId: string,
  attachmentId: string
) {
  const url = CSIurl.ITEMS_ID_ATTACHMENTS_ID.replace("{item_id}", itemId).replace(
    "{id}",
    attachmentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  return await csiAPI.delete(url, { signal, customConfig })
}

export async function getItemAttachmentContent(
  config: HttpConfig = {},
  item_id: string,
  attachmentId: string
): Promise<Blob> {
  const url = CSIurl.ITEMS_ID_ATTACHMENTS_ID_CONTENT.replace("{item_id}", item_id).replace(
    "{id}",
    attachmentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { headers, signal, responseType: "blob", customConfig })

  return data
}

export async function postItemAttachmentBatch(
  config: HttpConfig = {},
  item_id: string,
  attachments: AttachmentUpload[]
): Promise<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment>> {
  const url = CSIurl.ITEMS_ID_ATTACHMENTS_BATCH.replace("{item_id}", item_id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = { "Content-Type": "multipart/form-data" }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const formData = new FormData()

  for (const attachment of attachments) {
    appendAttachmentToFormData(attachment, formData)
  }

  const response = await csiAPI.post(url, formData, { headers, signal, customConfig })

  return handleAttachmentBatchResponses(response, "item")
}

//endregion

//region Item Work Orders

/**
 * Returns the list of all the Work Order for the given itemId
 *
 * @param config custom configurations and filtering params
 * @param itemId
 */
export async function getWorkOrderListForItem(
  config: HttpConfig = {},
  itemId: string
): Promise<ObjectList<WorkOrderRegularItem>> {
  const entityURL = CSIurl.ITEMS_WORK_ORDER_REGULAR.replace("{item_id}", itemId)
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

//endregion
